import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Coins, Loader2, ExternalLink, CheckCircle2, ShieldCheck, Wallet, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import {
  TOKENS,
  ACTIVE_NETWORK,
  sendCrypto,
  fetchUsdPrice,
  explorerTxUrl,
  isValidEvmAddress,
  shortAddr,
} from "@/lib/cryptoSend";
import { isUserRejection } from "@/lib/walletSign";
import { connectWallet } from "@/lib/walletSign";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

// Human-readable messages for the backend's on-chain verification error codes.
const CONFIRM_ERRORS = {
  tx_not_found: "We couldn't find that transaction on-chain yet.",
  tx_mismatch: "The transaction doesn't match this transfer.",
  tx_failed: "The transaction failed on-chain.",
  invalid_tx_hash: "Invalid transaction hash.",
  amount_precision: "Amount precision didn't match.",
  rpc_unavailable: "The network is busy — verification timed out.",
};

// In-chat crypto send. The user's own wallet signs & broadcasts (Base Sepolia testnet); the backend
// then verifies the tx on-chain (~1–2s) before posting an in-thread receipt + notifying the recipient.
export function CryptoSendDialog({ open, onOpenChange, recipient, context }) {
  const [token, setToken] = useState(TOKENS[0]);
  const [amount, setAmount] = useState("");
  const [toAddress, setToAddress] = useState("");
  const [manual, setManual] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | signing | verifying
  const [result, setResult] = useState(null); // { tx_hash, status, explorer_url, posted } on success
  const [verifyErr, setVerifyErr] = useState(null); // { hash, code, retryable }
  const [lastSent, setLastSent] = useState(null); // { hash, from } — kept for retry
  const [source, setSource] = useState("browser"); // "browser" | "wc" (Qastle · WalletConnect)

  // Resolve the recipient's on-chain address (404 → they haven't linked one → manual paste).
  const { data: rw, isLoading: rwLoading } = useQuery({
    queryKey: ["receiving-wallet", recipient?.id],
    queryFn: () => api.receivingWallet(recipient.id, ACTIVE_NETWORK.key),
    enabled: open && !!recipient?.id,
    retry: false,
  });

  // Live USD price for volatile tokens; stablecoins pinned to $1.
  const { data: price } = useQuery({
    queryKey: ["usd-price", token.coingeckoId],
    queryFn: () => fetchUsdPrice(token.coingeckoId),
    enabled: open && !token.stable,
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    if (!open) {
      setAmount(""); setResult(null); setVerifyErr(null); setLastSent(null); setPhase("idle"); setManual(false); setSource("browser");
    }
  }, [open]);

  useEffect(() => {
    if (rw?.address && isValidEvmAddress(rw.address)) { setToAddress(rw.address); setManual(false); }
    else if (!rwLoading && open && recipient?.id) { setManual(true); }
  }, [rw, rwLoading, open, recipient?.id]);

  const resolved = !!rw?.address && !manual;
  const rwVerified = resolved && !!rw?.verified;
  const usd = token.stable ? Number(amount || 0) : Number(amount || 0) * (price || 0);
  const name = recipient?.display_name || (recipient?.handle ? `@${recipient.handle}` : "them");
  const busy = phase !== "idle";

  const runConfirm = async (hash, from) => {
    setPhase("verifying");
    setVerifyErr(null);
    try {
      const res = await api.cryptoConfirm({
        recipient_id: recipient?.id,
        chain: ACTIVE_NETWORK.key,
        token: token.kind === "native" ? "native" : token.address,
        amount,
        tx_hash: hash,
        from_address: from,
        context,
      });
      setResult({
        tx_hash: hash,
        status: res?.status || "pending",
        explorer_url: res?.explorer_url || explorerTxUrl(hash),
        posted: res?.posted,
      });
      setPhase("idle");
      toast.success(`Sent ${amount} ${token.symbol} 🎉`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const code = (detail && typeof detail === "object" ? detail.error : null) || e?.response?.data?.error || detail;
      if (status === 409 || code === "already_confirmed") {
        setResult({ tx_hash: hash, status: "confirmed", explorer_url: explorerTxUrl(hash), posted: undefined });
        setPhase("idle");
        return;
      }
      const retryable = status === 502 || code === "rpc_unavailable";
      setVerifyErr({ hash, code: code || "verify_failed", retryable });
      setPhase("idle");
    }
  };

  const doSend = async () => {
    if (!isValidEvmAddress(toAddress)) return toast.error("Enter a valid 0x recipient address.");
    if (!(Number(amount) > 0)) return toast.error("Enter an amount greater than 0.");
    setPhase("signing");
    setVerifyErr(null);
    let sent;
    try {
      let provider;
      if (source === "wc") {
        const signer = await connectWallet("evm", { via: "walletconnect" });
        provider = signer.provider;
      }
      sent = await sendCrypto({ token, to: toAddress, amount, provider });
    } catch (e) {
      setPhase("idle");
      if (isUserRejection(e)) toast.info("Transaction canceled.");
      else toast.error(e?.message || "Couldn't send — check your wallet and network.");
      return;
    }
    setLastSent(sent);
    await runConfirm(sent.hash, sent.from);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white sm:max-w-sm" data-testid="crypto-send-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Coins className="h-5 w-5 text-bloq-mint" /> Send crypto
          </DialogTitle>
          <DialogDescription className="text-sm text-bloq-muted">
            Send {name} tokens straight to their wallet — no middleman.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 py-2 text-center" data-testid="crypto-send-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-bloq-mint" />
            <div>
              <div className="font-heading text-lg font-bold">Sent {amount} {token.symbol}</div>
              <div className="text-sm text-bloq-muted">
                {result.status === "confirmed" ? "Confirmed on-chain ✅" : "Broadcast — confirming on-chain…"}
                {result.posted === false && " Receipt will post once you both have a chat open."}
              </div>
            </div>
            <a
              href={result.explorer_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-bloq-mint hover:underline"
              data-testid="crypto-tx-link"
            >
              View on BaseScan <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <Button onClick={() => onOpenChange(false)} className="h-11 w-full rounded-full bloq-gradient font-bold text-bloq-bg" data-testid="crypto-done-btn">Done</Button>
          </div>
        ) : verifyErr ? (
          <div className="space-y-4 py-2 text-center" data-testid="crypto-verify-error">
            <AlertTriangle className="mx-auto h-12 w-12 text-amber-400" />
            <div>
              <div className="font-heading text-lg font-bold">Sent — verifying failed</div>
              <div className="text-sm text-bloq-muted">
                Your {amount} {token.symbol} transfer was broadcast to the network, but we couldn't verify it: {CONFIRM_ERRORS[verifyErr.code] || "please check the explorer."}
              </div>
            </div>
            <a
              href={explorerTxUrl(verifyErr.hash)} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-bloq-mint hover:underline"
              data-testid="crypto-verify-tx-link"
            >
              View on BaseScan <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <div className="flex gap-2">
              {verifyErr.retryable && (
                <Button
                  onClick={() => lastSent && runConfirm(lastSent.hash, lastSent.from)}
                  className="h-11 flex-1 rounded-full bloq-gradient font-bold text-bloq-bg"
                  data-testid="crypto-retry-verify-btn"
                >
                  Retry
                </Button>
              )}
              <Button onClick={() => onOpenChange(false)} variant="outline" className="h-11 flex-1 rounded-full border-white/15 bg-transparent text-white hover:bg-white/5" data-testid="crypto-verify-done-btn">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300" data-testid="crypto-testnet-badge">
              {ACTIVE_NETWORK.name} · testnet
            </span>

            {/* Recipient */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Recipient</Label>
              {resolved ? (
                <div className="flex items-center gap-2 rounded-xl border border-bloq-mint/30 bg-bloq-mint/[0.06] px-3 py-2.5 text-sm" data-testid="crypto-recipient-resolved">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-bloq-mint" />
                  <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
                  {rwVerified && <span className="shrink-0 rounded-full bg-bloq-mint/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-bloq-mint" data-testid="crypto-recipient-verified">verified</span>}
                  <span className="shrink-0 font-mono text-xs text-bloq-muted">{shortAddr(toAddress)}</span>
                  <button type="button" onClick={() => setManual(true)} className="shrink-0 text-[11px] font-semibold text-bloq-mint hover:underline" data-testid="crypto-edit-address">edit</button>
                </div>
              ) : (
                <Input
                  value={toAddress}
                  onChange={(e) => setToAddress(e.target.value)}
                  placeholder="0x… recipient address"
                  className="h-11 rounded-xl border-white/12 bg-bloq-bg font-mono text-sm"
                  data-testid="crypto-address-input"
                />
              )}
              {!resolved && recipient?.id && (
                <p className="text-[11px] text-bloq-muted">{name} hasn't linked a public wallet yet — paste an address to send.</p>
              )}
            </div>

            {/* Token */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Token</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="crypto-token-select">
                {TOKENS.map((t) => (
                  <button
                    key={t.symbol}
                    type="button"
                    onClick={() => setToken(t)}
                    className={cn(
                      "flex items-center justify-center gap-1.5 rounded-xl border py-2.5 text-sm font-bold transition-colors",
                      token.symbol === t.symbol ? "border-bloq-mint/60 bg-bloq-mint/15 text-bloq-mint" : "border-white/10 text-bloq-muted hover:text-white",
                    )}
                    data-testid={`crypto-token-${t.symbol}`}
                  >
                    {t.symbol}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Amount</Label>
              <div className="relative">
                <Input
                  type="number" min="0" step="any" inputMode="decimal"
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00" className="h-12 rounded-xl border-white/12 bg-bloq-bg pr-16 text-lg font-bold"
                  data-testid="crypto-amount-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold text-bloq-muted">{token.symbol}</span>
              </div>
              {usd > 0 && (
                <p className="text-[11px] text-bloq-muted" data-testid="crypto-usd-estimate">≈ ${usd.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD</p>
              )}
            </div>

            {/* Wallet source */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Pay with</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="crypto-source-select">
                {[
                  { id: "browser", label: "Browser wallet" },
                  { id: "wc", label: "Qastle · WalletConnect" },
                ].map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSource(s.id)}
                    className={cn(
                      "rounded-xl border py-2.5 text-xs font-bold transition-colors",
                      source === s.id ? "border-bloq-mint/60 bg-bloq-mint/15 text-bloq-mint" : "border-white/10 text-bloq-muted hover:text-white",
                    )}
                    data-testid={`crypto-source-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={doSend}
              disabled={busy || !isValidEvmAddress(toAddress) || !(Number(amount) > 0)}
              className="h-12 w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90 disabled:opacity-50"
              data-testid="crypto-send-btn"
            >
              {phase === "signing" ? (
                <><Loader2 className="mr-1.5 h-5 w-5 animate-spin" /> Confirm in your wallet…</>
              ) : phase === "verifying" ? (
                <><Loader2 className="mr-1.5 h-5 w-5 animate-spin" /> Verifying on-chain…</>
              ) : (
                <><Wallet className="mr-1.5 h-5 w-5" /> Send {amount ? `${amount} ${token.symbol}` : token.symbol}</>
              )}
            </Button>
            <p className="text-center text-[10px] text-bloq-muted">Your wallet signs the transfer. BLOQ never holds your funds.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
